// src/lib/prospect-filters.js — logique pure de filtrage/tri de la Prospection.
//
// CHANTIER 61 (Wagon I, reprise du 43). Centralise la dérivation de l'état
// "contacté" et le tri/filtre/dédup, unit-testable (resetMocks → beforeEach
// côté tests). LOT 4 OPÉRATEUR B : ProspectionTab est supprimé — le consommateur
// vivant est la vue Logements › « À démarcher » (ADemarcherCtrl, isMasque).
//
// DÉCISIONS MÉTIER actées :
// - Le booléen "Contacté ?" est DÉRIVÉ d'une trace de contact RÉELLE
//   (emailEnvoye OU date d'email OU au moins une interaction), PAS du champ
//   `statut`. Raison : `statut: 'contacte'` est historiquement le statut PAR
//   DÉFAUT du scraper (= candidat à la prospection auto), pas une preuve qu'on
//   a réellement contacté le prospect. Les ~1000 prospects scrappés sans trace
//   apparaissent donc "Pas contacté", ce qui est la réalité métier — sans
//   toucher à la donnée `statut` sous-jacente (réversible, zéro write).
// - "Masqué" = annonce morte/doublon retirée de la vue : soit masquage manuel
//   (`masque === true`), soit fraîcheur dépassée (last_seen_at vieux de N jours,
//   alimenté par le scraper). Tant que le scraper n'a pas re-stampé last_seen_at
//   sur le parc existant, seul le masquage manuel agit (limite documentée).

// Fenêtre de fraîcheur : une annonce non revue par le scraper depuis N jours est
// considérée morte. 14 j = ~28 passages de cron (2x/j) sans la revoir. Aligné sur
// AUTOMASK_DAYS (lib/prospects-store) qui PERSISTE masque=true côté serveur : le
// front et la passe serveur jugent la péremption sur le même seuil.
export const STALE_DAYS = 14

// Trace de contact réelle (≠ statut par défaut du scraper).
export function isContacte(p) {
  if (!p) return false
  if (p.emailEnvoye === true) return true
  if (p.dateEmailEnvoye) return true
  if (Array.isArray(p.interactions) && p.interactions.length > 0) return true
  return false
}

// Annonce masquée : manuel OU périmée (fraîcheur). `now` injectable pour les tests.
export function isStale(p, now = Date.now(), staleDays = STALE_DAYS) {
  if (!p || !p.lastSeenAt) return false // pas de signal de fraîcheur → jamais périmé
  const seen = Date.parse(p.lastSeenAt)
  if (Number.isNaN(seen)) return false
  return now - seen > staleDays * 24 * 60 * 60 * 1000
}

export function isMasque(p, now = Date.now(), staleDays = STALE_DAYS) {
  if (!p) return false
  if (p.masque === true) return true
  return isStale(p, now, staleDays)
}

// URL d'annonce normalisée (clé de dédup la plus stable disponible : `lien`).
// Retire les query-params de tracking et le slash final pour matcher les variantes.
export function normalizeLien(lien) {
  if (!lien || typeof lien !== 'string') return ''
  let s = lien.trim().toLowerCase()
  const q = s.indexOf('?')
  if (q !== -1) s = s.slice(0, q)
  const h = s.indexOf('#')
  if (h !== -1) s = s.slice(0, h)
  s = s.replace(/\/+$/, '')
  return s
}

// "Richesse" d'un prospect pour choisir le survivant lors d'un doublon par URL :
// on garde celui qui porte le plus d'info / de contact (un contacté > non contacté,
// plus d'interactions > moins, présence d'email).
function richness(p) {
  let r = 0
  if (isContacte(p)) r += 100
  if (Array.isArray(p.interactions)) r += p.interactions.length
  if (p.email && String(p.email).trim()) r += 1
  return r
}

// Dédoublonnage par URL d'annonce (`lien`). Les prospects sans `lien` sont tous
// conservés (pas de clé stable → on ne fusionne pas à l'aveugle). Préserve
// l'ordre d'apparition pour la 1ʳᵉ occurrence retenue de chaque URL.
export function dedupeByLien(prospects) {
  const list = Array.isArray(prospects) ? prospects : []
  const bestByLien = new Map()
  const noLien = []
  const order = []
  for (const p of list) {
    const key = normalizeLien(p.lien)
    if (!key) { noLien.push(p); continue }
    if (!bestByLien.has(key)) {
      bestByLien.set(key, p)
      order.push(key)
    } else {
      const cur = bestByLien.get(key)
      if (richness(p) > richness(cur)) bestByLien.set(key, p)
    }
  }
  const deduped = order.map(k => bestByLien.get(k))
  return [...deduped, ...noLien]
}

// Compteur de doublons retirés (pour affichage admin).
export function countDuplicates(prospects) {
  const list = Array.isArray(prospects) ? prospects : []
  return list.length - dedupeByLien(list).length
}

const matchesSearch = (p, search) => {
  const q = (search || '').trim().toLowerCase()
  if (!q) return true
  return (p.nom || '').toLowerCase().includes(q) || (p.adresse || '').toLowerCase().includes(q)
}

// Filtre + tri principal. Options :
//   type:    'tous' | 'proprietaire' | 'agence'
//   contact: 'tous' | 'pas_contacte' | 'contacte'
//   sortBy:  'score' | 'date'
//   search:  string
//   includeMasques: bool (false = cache les annonces mortes/doublons par défaut)
//   dedupe:  bool (true = applique dedupeByLien)
//   now:     ms (injectable tests)
//   indexById: Map id→index (proxy chronologique pour le tri date / tie-break)
export function filterAndSortProspects(prospects, opts = {}) {
  const {
    type = 'tous', contact = 'tous', sortBy = 'score', search = '',
    includeMasques = false, dedupe = true, now = Date.now(),
    indexById = null, staleDays = STALE_DAYS,
  } = opts
  let list = Array.isArray(prospects) ? prospects : []
  if (dedupe) list = dedupeByLien(list)
  const idx = indexById || new Map(list.map((p, i) => [p.id, i]))

  const filtered = list.filter(p => {
    if (!includeMasques && isMasque(p, now, staleDays)) return false
    if (type !== 'tous' && p.type !== type) return false
    if (contact === 'contacte' && !isContacte(p)) return false
    if (contact === 'pas_contacte' && isContacte(p)) return false
    return matchesSearch(p, search)
  })

  filtered.sort((a, b) => {
    if (sortBy === 'score') {
      return (b.score || 0) - (a.score || 0) || (idx.get(b.id) || 0) - (idx.get(a.id) || 0)
    }
    return (idx.get(b.id) || 0) - (idx.get(a.id) || 0)
  })
  return filtered
}
